import { getColorStyle } from '@common/color';
import { convertCurrentDate } from '@common/date-helpers';
import { formatText } from '@common/text-helpers';
import { getValue } from '@utils/lodash';
import { useState } from 'react';

function CommonApproverFlowPopup(props: any) {
  const { showUpdatePopup, setShowUpdatePopup } = props;
  const wrappedTextStyle = {
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
    whiteSpace: 'pre-wrap' as const,
  };

  const metaRowStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    marginTop: '4px',
  };

  const commentBoxStyle = {
    marginTop: '10px',
    padding: '10px 12px',
    borderRadius: '8px',
    background: '#F8FAFD',
    border: '1px solid #E3E8EF',
  };

  const [scrollPositions, setScrollPositions] = useState<{
    [key: string]: number;
  }>({});

  let currentApproverId =
    getValue(props, 'current_approver.id', null) || props.currentApproverId;

  if (!currentApproverId) {
    const approvers = getValue(props, `approvers`, []);
    const currentApprover = approvers.find(
      (app: any) =>
        getValue(app, 'submitted_at', '') &&
        !getValue(app, 'approved_at', '') &&
        getValue(app, 'approval_status', '') === 'awaiting_approval',
    );
    if (currentApprover) {
      currentApproverId = getValue(currentApprover, 'approver.id', null);
    }
  }

  const isCurrentApprover = (
    approverId: number | null,
    approverData: any = null,
  ): boolean => {
    const currentId = Number(currentApproverId);
    const checkId = Number(approverId);

    if (!(currentId && checkId && currentId === checkId)) {
      return false;
    }

    if (approverData) {
      const hasSubmittedAt = !!getValue(approverData, 'submitted_at', '');
      const hasApprovedAt = !!getValue(approverData, 'approved_at', '');
      const approvalStatus = getValue(approverData, 'approval_status', '');

      return (
        hasSubmittedAt &&
        !hasApprovedAt &&
        approvalStatus === 'awaiting_approval'
      );
    }

    return true;
  };

  const getStepColors = (
    value: number,
    isCurrentApprover: boolean = false,
    isApproved: boolean = false,
    isSkipped: boolean = false,
  ) => {
    if (isApproved) {
      return 'rgb(34, 179, 120)';
    }

    if (isSkipped) {
      return '#8B5CF6';
    }

    if (isCurrentApprover) {
      switch (value) {
        case 1:
          return '#FEF5E7';
        case 2:
          return '#FFEBD0';
        case 3:
          return '#FFDFB6';
        case 4:
          return '#FFD4A0';
        case 5:
          return '#FFC987';
        case 6:
          return '#FFBD71';
        case 7:
          return '#FFB25B';
        case 8:
          return '#FFA746';
        case 9:
          return '#FF9B30';
        case 10:
          return '#FF8F1A';
        default:
          return '#FF8F1A';
      }
    }

    return '#f5f5f5';
  };

  const getBorderColor = (
    isCurrentApprover: boolean,
    isApproved: boolean,
    isSkipped: boolean,
  ) => {
    if (isApproved) return 'rgb(34, 179, 120)';
    if (isSkipped) return '#8B5CF6';
    if (isCurrentApprover) return '#FF8F1A';
    return '#ddd';
  };

  const getTextColor = (
    isCurrentApprover: boolean,
    isApproved: boolean,
    isSkipped: boolean,
  ) => {
    if (isApproved) return 'white';
    if (isSkipped) return 'white';
    if (isCurrentApprover) return '#D35400';
    return '#999';
  };

  const groupApproversByLevel = (
    approvers: any[],
  ): { [key: string]: any[] } => {
    const grouped: { [key: string]: any[] } = {};

    if (!approvers || !Array.isArray(approvers)) return grouped;

    approvers.forEach((approver) => {
      const level = getValue(approver, 'approval_level', '');
      if (!grouped[level]) {
        grouped[level] = [];
      }
      grouped[level].push(approver);
    });

    return grouped;
  };

  const getDelegatedUserLabel = (approver: any): string => {
    const delegatedFirstName = getValue(
      approver,
      'delegated_user.first_name',
      '',
    );
    const delegatedLastName = getValue(
      approver,
      'delegated_user.last_name',
      '',
    );
    const fullName = `${delegatedFirstName} ${delegatedLastName}`
      .replace(/\s+/g, ' ')
      .trim();

    if (fullName) return fullName;

    const delegatedUserName = getValue(approver, 'delegated_user_name', '');
    if (delegatedUserName) return delegatedUserName;

    const delegatedEmail = getValue(approver, 'delegated_user.email', '');
    if (delegatedEmail) return delegatedEmail;

    return '';
  };

  const getDelegatedUsersForApprovers = (approvers: any[]): string[] => {
    if (!approvers || !Array.isArray(approvers)) return [];
    const labels = approvers
      .map((approver) => getDelegatedUserLabel(approver))
      .filter((label) => Boolean(label));
    return Array.from(new Set(labels));
  };

  const getApprovalHistory = (): any[] => {
    return (
      getValue(props, 'approvalHistory', []) ||
      getValue(props, 'approval_history', []) ||
      []
    );
  };

  const getDelegatedUsersFromHistory = (
    approvers: any[],
    level: string,
  ): string[] => {
    const history = getApprovalHistory();
    if (!history || !Array.isArray(history) || history.length === 0) return [];

    const approverIds = approvers
      .map((app) => getValue(app, 'approver.id', null))
      .filter((id) => id !== null);

    const hasLevelKey = history.some(
      (item) => getValue(item, 'approval_level', '') !== '',
    );
    const levelMatches = (item: any) => {
      if (!hasLevelKey) return false;
      return String(getValue(item, 'approval_level', '')) === String(level);
    };

    const historyForLevelOrApprovers = history.filter((item) => {
      if (levelMatches(item)) return true;
      const historyApproverId = getValue(item, 'approver.id', null);
      return historyApproverId && approverIds.includes(historyApproverId);
    });

    const labels = historyForLevelOrApprovers
      .map((item) => getDelegatedUserLabel(item))
      .filter((label) => Boolean(label));

    return Array.from(new Set(labels));
  };

  const getDelegatedUsersForLevel = (
    approvers: any[],
    level: string,
  ): string[] => {
    const fromApprovers = getDelegatedUsersForApprovers(approvers);
    const fromHistory = getDelegatedUsersFromHistory(approvers, level);
    return Array.from(new Set([...fromApprovers, ...fromHistory]));
  };

  const handleScroll = (level: string, direction: 'left' | 'right') => {
    const container = document.getElementById(`scroll-container-${level}`);
    if (container) {
      const scrollAmount = 220;
      const currentScroll = container.scrollLeft;
      const newScroll =
        direction === 'left'
          ? Math.max(0, currentScroll - scrollAmount)
          : currentScroll + scrollAmount;

      container.scrollTo({
        left: newScroll,
        behavior: 'smooth',
      });

      setScrollPositions((prev) => ({
        ...prev,
        [level]: newScroll,
      }));
    }
  };

  const canScrollLeft = (level: string) => {
    return (scrollPositions[level] || 0) > 0;
  };

  const canScrollRight = (level: string, approvers: any[]) => {
    const container = document.getElementById(`scroll-container-${level}`);
    if (container) {
      const maxScroll = container.scrollWidth - container.clientWidth;
      return (scrollPositions[level] || 0) < maxScroll;
    }
    return approvers.length > 2;
  };

  const renderOriginalApproverCard = (approvers: any[], level: string) => {
    const currentApprover = approvers[0];
    const approverId = getValue(currentApprover, 'approver.id', null);
    const delegatedUsersForLevel = getDelegatedUsersForLevel(approvers, level);

    const hasApprovedApprover = approvers.some(
      (app) => getValue(app, 'approval_status', '') === 'approved',
    );

    const hasSkippedApprover = approvers.some(
      (app) => getValue(app, 'approval_status', '') === 'skipped',
    );

    const displayStatus = hasApprovedApprover
      ? 'approved'
      : hasSkippedApprover
        ? 'skipped'
        : 'awaiting_approval';

    const isCurrentApproverCard = isCurrentApprover(
      approverId,
      currentApprover,
    );
    const isApproved = displayStatus === 'approved';
    const isSkipped = displayStatus === 'skipped';

    return (
      <div
        key={level}
        className="approver_flow_container m-3 p-3 position-relative"
        style={{
          borderLeft: isApproved
            ? '3px solid rgb(34, 179, 120)'
            : isCurrentApproverCard || isSkipped
              ? '3px solid #408dfb'
              : '3px solid #ddd',
          backgroundColor: '#fff',
          borderRadius: '6px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        <div className="d-flex ms-3 align-items-center">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {!isApproved ? (
              <>
                <div
                  className="profile_circle"
                  style={{
                    background: getStepColors(
                      parseInt(level),
                      isCurrentApproverCard || isSkipped,
                      isApproved,
                      false,
                    ),
                    border: `1px solid ${getBorderColor(isCurrentApproverCard || isSkipped, isApproved, false)}`,
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <h5
                    className="title"
                    style={{
                      color: getTextColor(
                        isCurrentApproverCard || isSkipped,
                        isApproved,
                        false,
                      ),
                      margin: 0,
                    }}
                  >
                    {level}
                  </h5>
                </div>
                <div
                  style={{
                    marginTop: '5px',
                    fontSize: '14px',
                    color: '#333',
                  }}
                >
                  Level {level}
                </div>
              </>
            ) : (
              <>
                <div
                  className="profile_circle"
                  style={{
                    backgroundColor: isApproved
                      ? 'rgb(34, 179, 120)'
                      : isSkipped
                        ? '#8B5CF6'
                        : 'rgb(34, 179, 120)',
                    border: `1px solid ${isApproved ? 'rgb(34, 179, 120)' : isSkipped ? '#8B5CF6' : 'rgb(34, 179, 120)'}`,
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    style={{
                      color: 'white',
                      fontSize: '8px',
                      fontWeight: 'bold',
                    }}
                  >
                    {isSkipped ? (
                      <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        S
                      </span>
                    ) : (
                      <img
                        src="/images/white-check.svg"
                        className="img-fluid navigation-bar-wrapper__close-btn"
                        style={{ filter: 'brightness(0) invert(1)' }}
                        onClick={() => {
                          setShowUpdatePopup(false);
                        }}
                        alt=""
                      />
                    )}
                  </span>
                </div>
                <div
                  style={{
                    marginTop: '5px',
                    fontSize: '14px',
                    color: '#333',
                  }}
                >
                  Level {level}
                </div>
              </>
            )}
          </div>

          <div className="ms-2 flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <h6
                  className="header_text__17"
                  style={{
                    color: '#333',
                    ...wrappedTextStyle,
                  }}
                >
                  {getValue(currentApprover, `approver.first_name`, '')}{' '}
                  {getValue(currentApprover, `approver.middle_name`, '') &&
                    getValue(currentApprover, `approver.middle_name`, '') +
                      ' '}{' '}
                  {getValue(currentApprover, `approver.last_name`, '')}
                </h6>
                <p
                  className="small_text"
                  style={{
                    color: '#666',
                    ...wrappedTextStyle,
                  }}
                >
                  {getValue(currentApprover, `approver.email`, '')}
                </p>
                {delegatedUsersForLevel.length > 0 && (
                  <p
                    className="small_text__12"
                    style={{
                      color: '#6c757d',
                      margin: '4px 0 0 0',
                      ...wrappedTextStyle,
                    }}
                  >
                    Action performed by delegated user:{' '}
                    {delegatedUsersForLevel.join(', ')}
                  </p>
                )}
              </div>
            </div>

            {(getValue(currentApprover, `submitted_at`, '') ||
              getValue(currentApprover, `approved_at`, '')) && (
              <div className="border-radius__5 mt-3">
                {getValue(currentApprover, `submitted_at`, '') && (
                  <div style={metaRowStyle}>
                    <p
                      className="small_text__12"
                      style={{
                        color: '#666',
                      }}
                    >
                      Submitted On:
                    </p>
                    <p
                      className="header_text__12 ms-1"
                      style={{
                        color: '#333',
                        ...wrappedTextStyle,
                      }}
                    >
                      {convertCurrentDate(
                        getValue(currentApprover, `submitted_at`, ''),
                      )}
                    </p>
                  </div>
                )}
                {getValue(currentApprover, `approved_at`, '') && (
                  <div style={metaRowStyle}>
                    <p
                      className="small_text__12"
                      style={{
                        color: '#666',
                      }}
                    >
                      {isSkipped ? 'Skipped On:' : 'Approved On:'}
                    </p>
                    <p
                      className="header_text__12 ms-1"
                      style={{
                        color: '#333',
                        ...wrappedTextStyle,
                      }}
                    >
                      {convertCurrentDate(
                        getValue(currentApprover, `approved_at`, ''),
                      )}
                    </p>
                  </div>
                )}

                {getValue(currentApprover, `approver_comment`, '') && (
                  <div style={commentBoxStyle}>
                    <p
                      className="small_text__12"
                      style={{
                        color: '#666',
                        marginBottom: '6px',
                        fontWeight: 600,
                      }}
                    >
                      Comments:
                    </p>
                    <p
                      className="header_text__12"
                      style={{
                        color: '#333',
                        margin: 0,
                        ...wrappedTextStyle,
                      }}
                    >
                      {getValue(currentApprover, `approver_comment`, '')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <h6
          className="header_text status-poistion-absolute"
          style={getColorStyle(displayStatus)}
        >
          {formatText(displayStatus)}
        </h6>
      </div>
    );
  };

  const renderApproverCard = (
    approver: any,
    isLast: boolean,
    hasSkippedAtLevel: boolean = false,
  ) => {
    const approvalStatus = getValue(approver, 'approval_status', '');
    const isApproved = approvalStatus === 'approved';
    const isSkipped = approvalStatus === 'skipped';
    const delegatedUserLabel = getDelegatedUserLabel(approver);
    const getScrollCardBorderColor = () => {
      if (isApproved) return 'rgb(34, 179, 120)';
      if (isSkipped && hasSkippedAtLevel) return '#ddd';
      if (isSkipped) return '#8B5CF6';
      return '#ddd';
    };

    return (
      <div
        className="approver-card"
        style={{
          minWidth: '200px',
          maxWidth: '260px',
          padding: '16px',
          backgroundColor: '#fff',
          border: `1px solid ${getScrollCardBorderColor()}`,
          borderRadius: '8px',
          position: 'relative',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        <h6
          style={{
            fontSize: '14px',
            fontWeight: '600',
            margin: '0 0 4px 0',
            color: '#333',
            lineHeight: '1.3',
            ...wrappedTextStyle,
          }}
        >
          {getValue(approver, `approver.first_name`, '')}{' '}
          {getValue(approver, `approver.middle_name`, '') &&
            getValue(approver, `approver.middle_name`, '') + ' '}{' '}
          {getValue(approver, `approver.last_name`, '')}
        </h6>

        <p
          style={{
            fontSize: '12px',
            color: '#666',
            margin: '0 0 8px 0',
            ...wrappedTextStyle,
          }}
        >
          {getValue(approver, `approver.email`, '')}
        </p>
        {delegatedUserLabel && (
          <p
            style={{
              fontSize: '11px',
              color: '#6c757d',
              margin: '0 0 8px 0',
              ...wrappedTextStyle,
            }}
          >
            Action performed by delegated user: {delegatedUserLabel}
          </p>
        )}

        {(getValue(approver, `submitted_at`, '') ||
          getValue(approver, `approved_at`, '') ||
          getValue(approver, `approver_comment`, '')) && (
          <div
            style={{
              fontSize: '11px',
              color: '#888',
            }}
          >
            {getValue(approver, `submitted_at`, '') && (
              <div style={{ marginBottom: '2px', ...wrappedTextStyle }}>
                <strong>Submitted:</strong>{' '}
                {convertCurrentDate(getValue(approver, `submitted_at`, ''))}
              </div>
            )}
            {getValue(approver, `approved_at`, '') && (
              <div style={{ marginBottom: '2px', ...wrappedTextStyle }}>
                <strong>{isSkipped ? 'Skipped:' : 'Approved:'}</strong>{' '}
                {convertCurrentDate(getValue(approver, `approved_at`, ''))}
              </div>
            )}
            {getValue(approver, `approver_comment`, '') && (
              <div style={commentBoxStyle}>
                <strong style={{ display: 'block', marginBottom: '6px' }}>
                  Comments:
                </strong>
                <span style={wrappedTextStyle}>
                  {getValue(approver, `approver_comment`, '')}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderMultipleApproversCard = (approvers: any[], level: string) => {
    const hasApprovedApprover = approvers.some(
      (app) => getValue(app, 'approval_status', '') === 'approved',
    );

    const hasSkippedApprover = approvers.some(
      (app) => getValue(app, 'approval_status', '') === 'skipped',
    );

    const hasCurrentApprover = approvers.some((app) =>
      isCurrentApprover(getValue(app, 'approver.id', null), app),
    );

    const displayStatus = hasApprovedApprover
      ? 'approved'
      : hasSkippedApprover
        ? 'skipped'
        : 'awaiting_approval';

    const activeApprovers = approvers.filter(
      (app) => getValue(app, 'approval_status', '') !== 'auto_closed',
    );
    const delegatedUsersForLevel = getDelegatedUsersForLevel(approvers, level);

    if (activeApprovers.length === 1) {
      return renderOriginalApproverCard(activeApprovers, level);
    }

    const isApproved = displayStatus === 'approved';
    const isSkipped = displayStatus === 'skipped';

    return (
      <div
        key={level}
        className="approver_flow_container m-3 p-3 position-relative"
        style={{
          borderLeft: isApproved
            ? '3px solid rgb(34, 179, 120)'
            : hasCurrentApprover || isSkipped
              ? '3px solid #408dfb'
              : '3px solid #ddd',
          backgroundColor: '#fff',
          borderRadius: '6px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        <div className="d-flex ms-3 align-items-center">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div
              className="profile_circle"
              style={{
                background: isApproved
                  ? 'rgb(34, 179, 120)'
                  : hasCurrentApprover || isSkipped
                    ? getStepColors(parseInt(level), true, false, false)
                    : '#f5f5f5',
                border: `1px solid ${
                  isApproved
                    ? 'rgb(34, 179, 120)'
                    : hasCurrentApprover || isSkipped
                      ? '#FF8F1A'
                      : '#ddd'
                }`,
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isApproved ? (
                <img
                  src="/images/white-check.svg"
                  style={{
                    width: '12px',
                    height: '12px',
                    filter: 'brightness(0) invert(1)',
                  }}
                  alt="approved"
                />
              ) : isSkipped ? (
                <span
                  style={{
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  S
                </span>
              ) : (
                <h5
                  style={{
                    color: hasCurrentApprover || isSkipped ? '#D35400' : '#999',
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  {level}
                </h5>
              )}
            </div>
            <div
              style={{
                marginTop: '5px',
                fontSize: '14px',
                color: '#333',
              }}
            >
              Level {level}
            </div>
          </div>

          <div className="ms-2 flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <h6
                  className="header_text__17"
                  style={{
                    color: '#333',
                    ...wrappedTextStyle,
                  }}
                >
                  {isApproved
                    ? 'Approved'
                    : isSkipped
                      ? 'Skipped'
                      : 'Approval Required'}
                </h6>
                <p
                  className="small_text"
                  style={{
                    color: '#666',
                    ...wrappedTextStyle,
                  }}
                >
                  {approvers.length} Manager{approvers.length > 1 ? 's' : ''}
                </p>
                {delegatedUsersForLevel.length > 0 && (
                  <p
                    className="small_text__12"
                    style={{
                      color: '#6c757d',
                      margin: '4px 0 0 0',
                      ...wrappedTextStyle,
                    }}
                  >
                    Action performed by delegated user:{' '}
                    {delegatedUsersForLevel.join(', ')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {activeApprovers.length > 1 && (
          <div
            style={{
              marginTop: '16px',
              marginLeft: '12px',
              position: 'relative',
            }}
          >
            <div style={{ position: 'relative', overflow: 'hidden' }}>
              <div
                id={`scroll-container-${level}`}
                style={{
                  display: 'flex',
                  overflowX: 'auto',
                  gap: '12px',
                  scrollBehavior: 'smooth',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  paddingBottom: '4px',
                  width: '100%',
                }}
                onScroll={(e) => {
                  const target = e.target as HTMLDivElement;
                  setScrollPositions((prev) => ({
                    ...prev,
                    [level]: target.scrollLeft,
                  }));
                }}
              >
                {activeApprovers.map((approver, index) =>
                  renderApproverCard(
                    approver,
                    index === activeApprovers.length - 1,
                    isSkipped,
                  ),
                )}
              </div>

              {canScrollLeft(level) && (
                <button
                  onClick={() => handleScroll(level, 'left')}
                  style={{
                    position: 'absolute',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #000',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  <img
                    src="/images/back-link.svg"
                    style={{
                      width: '12px',
                      height: '12px',
                      filter: 'brightness(0) saturate(100%)',
                    }}
                    alt="previous"
                  />
                </button>
              )}

              {canScrollRight(level, activeApprovers) && (
                <button
                  onClick={() => handleScroll(level, 'right')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #000',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  <img
                    src="/images/back-link.svg"
                    style={{
                      width: '12px',
                      height: '12px',
                      transform: 'rotate(180deg)',
                      filter: 'brightness(0) saturate(100%)',
                    }}
                    alt="next"
                  />
                </button>
              )}
            </div>
          </div>
        )}

        <h6
          className="header_text status-poistion-absolute"
          style={getColorStyle(displayStatus)}
        >
          {formatText(displayStatus)}
        </h6>
      </div>
    );
  };

  const approvers = getValue(props, `approvers`, []);

  const groupedApprovers = groupApproversByLevel(approvers);

  return (
    <div
      className={`compelte-task-wrapper ${
        showUpdatePopup ? 'compelte-task-wrapper--active' : ''
      }`}
      style={{ width: '30%' }}
    >
      <div className="compelte-task-wrapper__header d-flex align-items-center justify-content-between">
        <h6 className="compelte-task-wrapper__title">Approval Details</h6>
        <img
          src="/images/close-white.svg"
          className="img-fluid navigation-bar-wrapper__close-btn"
          onClick={() => {
            setShowUpdatePopup(false);
          }}
          alt=""
        />
      </div>
      <div className="approver_flow_main_container">
        {Object.keys(groupedApprovers).length > 0 &&
          Object.keys(groupedApprovers)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map((level) => {
              const levelApprovers = groupedApprovers[level];

              if (levelApprovers.length > 1) {
                return renderMultipleApproversCard(levelApprovers, level);
              } else {
                return renderOriginalApproverCard(levelApprovers, level);
              }
            })}
      </div>
    </div>
  );
}

export default CommonApproverFlowPopup;
